/**
 * Composant de saisie des paramètres - Calculatrice AEP
 */

import React from 'react';
import { Gauge, Clock, RotateCcw, Copy, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { 
  PRESSIONS_AMONT, 
  PRESSION_AVAL_MIN, 
  RENDEMENTS, 
  MODES_DUREE,
  MIN_PRESSURE_BARS,
  MAX_PRESSURE_BARS
} from '../../lib/aep-constants';

export const ParametersCard = ({
  pressionAmont,
  setPressionAmont,
  pressionExploitable,
  unite,
  handleUniteChange,
  debit,
  setDebit,
  debitsDisponibles,
  rendement,
  setRendement,
  mode,
  setMode,
  onReset,
  onCopy,
  copied
}) => {
  return (
    <Card className="border border-[#CBD5E1] rounded-sm shadow-none">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold text-[#0F172A] flex items-center gap-2">
          <Gauge className="h-5 w-5 text-[#1E3A8A]" />
          Paramètres de calcul
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Pression Amont */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium text-[#0F172A]">
              Pression amont
            </Label>
            <Badge 
              variant="secondary" 
              className="font-mono-data bg-[#E2E8F0] text-[#0F172A] rounded-sm"
              data-testid="pression-amont-badge"
            >
              {pressionAmont} bar
            </Badge>
          </div>
          <Slider
            data-testid="pression-slider"
            value={[pressionAmont]}
            onValueChange={(v) => setPressionAmont(v[0])}
            min={MIN_PRESSURE_BARS}
            max={MAX_PRESSURE_BARS}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-[#475569] font-mono-data">
            {PRESSIONS_AMONT.map(p => (
              <span key={p} className={p <= PRESSION_AVAL_MIN ? 'text-amber-600' : ''}>{p}</span>
            ))}
          </div>
        </div>

        {/* Affichage des pressions */}
        <div className="grid grid-cols-3 gap-3 p-3 bg-[#F1F5F9] rounded-sm border border-[#CBD5E1]">
          <div className="text-center">
            <div className="text-xs text-[#475569] uppercase tracking-wide">Amont</div>
            <div className="text-lg font-semibold text-[#0F172A] font-mono-data">{pressionAmont} bar</div>
          </div>
          <div className="text-center border-x border-[#CBD5E1]">
            <div className="text-xs text-amber-600 uppercase tracking-wide">Aval min.</div>
            <div className="text-lg font-semibold text-amber-600 font-mono-data">{PRESSION_AVAL_MIN} bar</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-[#059669] uppercase tracking-wide">Exploitable</div>
            <div className={`text-lg font-semibold font-mono-data ${pressionExploitable > 0 ? 'text-[#059669]' : 'text-red-500'}`}>
              {pressionExploitable} bar
            </div>
          </div>
        </div>

        <Separator className="bg-[#CBD5E1]" />

        {/* Unité de débit */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-[#0F172A]">
            Unité de débit
          </Label>
          <Select 
            value={unite} 
            onValueChange={handleUniteChange}
            data-testid="unite-select"
          >
            <SelectTrigger 
              className="w-full border-[#CBD5E1] rounded-sm font-mono-data"
              data-testid="unite-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="lmin" data-testid="unite-lmin">L/min</SelectItem>
              <SelectItem value="m3h" data-testid="unite-m3h">m³/h</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Débit */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-[#0F172A]">
            Débit ({unite === 'lmin' ? 'L/min' : 'm³/h'})
          </Label>
          
          {/* Champ de saisie libre */}
          <div className="flex gap-2">
            <Input
              type="number"
              min="0.1"
              step="0.1"
              value={debit}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                if (!isNaN(val) && val > 0) {
                  setDebit(val);
                }
              }}
              className="flex-1 border-[#CBD5E1] rounded-sm font-mono-data"
              data-testid="debit-input"
              placeholder="Saisir un débit"
            />
            <span className="flex items-center text-sm text-[#475569] font-mono-data min-w-[50px]">
              {unite === 'lmin' ? 'L/min' : 'm³/h'}
            </span>
          </div>
          
          {/* Suggestions de débits prédéfinis */}
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-[#475569] self-center mr-1">Suggestions :</span>
            {debitsDisponibles.map(d => (
              <Button
                key={d}
                variant={debit === d ? "default" : "outline"}
                size="sm"
                onClick={() => setDebit(d)}
                className={`h-7 px-3 text-xs font-mono-data rounded-sm ${
                  debit === d 
                    ? 'bg-[#1E3A8A] hover:bg-[#172554] text-white' 
                    : 'border-[#CBD5E1] text-[#0F172A] hover:bg-[#F1F5F9]'
                }`}
                data-testid={`debit-suggestion-${d}`}
              >
                {d}
              </Button>
            ))}
          </div>
        </div>

        <Separator className="bg-[#CBD5E1]" />

        {/* Rendement */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-[#0F172A]">
            Rendement de la turbine
          </Label>
          <Select 
            value={rendement.toString()} 
            onValueChange={(v) => setRendement(Number(v))}
            data-testid="rendement-select"
          >
            <SelectTrigger 
              className="w-full border-[#CBD5E1] rounded-sm font-mono-data"
              data-testid="rendement-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RENDEMENTS.map(r => (
                <SelectItem 
                  key={r} 
                  value={r.toString()}
                  data-testid={`rendement-${r}`}
                >
                  {r} %
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Mode de durée */}
        <div className="space-y-3">
          <Label className="text-sm font-medium text-[#0F172A] flex items-center gap-2">
            <Clock className="h-4 w-4 text-[#475569]" />
            Mode de durée
          </Label>
          <Select 
            value={mode} 
            onValueChange={setMode}
            data-testid="mode-select"
          >
            <SelectTrigger 
              className="w-full border-[#CBD5E1] rounded-sm"
              data-testid="mode-trigger"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(MODES_DUREE).map(([key, { label, heures }]) => (
                <SelectItem 
                  key={key} 
                  value={key}
                  data-testid={`mode-${key}`}
                >
                  {label} ({heures} h)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Separator className="bg-[#CBD5E1]" />

        {/* Boutons d'action */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            onClick={onReset}
            variant="outline"
            className="flex-1 min-w-[120px] border-[#CBD5E1] text-[#0F172A] hover:bg-[#F1F5F9] rounded-sm uppercase text-xs tracking-wide"
            data-testid="reset-button"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Réinitialiser
          </Button>
          <Button
            onClick={onCopy}
            className="flex-1 min-w-[120px] bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-sm uppercase text-xs tracking-wide"
            data-testid="copy-button"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Copié !
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copier
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ParametersCard;
